/**
 * ===========================================================================
 * DATA FRESHNESS POLICY  (IntraSync HQ web)
 * ===========================================================================
 *
 * Until this file existed, "how does this screen stay current?" was answered
 * page-by-page by whoever wrote the page. Some surfaces streamed, some polled
 * on a hand-picked interval, some only moved when you pressed Refresh — and
 * nothing said which was intended. That inconsistency is itself a defect: it
 * makes a dead subscription indistinguishable from a screen that was never
 * meant to be live.
 *
 * THE RULE (apply it when adding a surface, in this order):
 *
 *  1. LIVE — the default for any surface a *different actor* can change while
 *     you are looking at it: another staff member editing a client, an Azure
 *     DevOps webhook, the Read.ai ingest pipeline, the infrastructure reconcile
 *     sweep, `yarn catalog:sync`. Subscribe through `realtimeBus`. Live
 *     surfaces patch rows IN PLACE where the payload is complete enough
 *     (`clients`, `devops-event`) and reload where it is not (Meeting
 *     Intelligence, whose events are notifications rather than records).
 *
 *  2. LIVE + POLL — live plus a slow backstop poll, ONLY where the screen also
 *     shows server-COMPUTED rollups that no single event can update (the
 *     Command Center's aggregate counts; the notification unread count). The
 *     poll is a backstop, never the primary path: 60s, not 5s. If you find
 *     yourself wanting a fast interval, the surface wants an event instead.
 *
 *  3. POLL ONLY — for data with no event stream at all, where staleness is
 *     visibly wrong (System Health probes). 30s.
 *
 *  4. MANUAL — everything else, and this is a legitimate, deliberate answer:
 *     admin/config screens, one-shot reports, and any list whose contents only
 *     change because the person looking at it changed them. These re-pull after
 *     their own writes and have a header Refresh button. Do NOT add a live
 *     subscription to a manual surface just because it is possible — every
 *     subscription is a listener that has to be torn down on route exit.
 *
 * WHAT IS NOT A FRESHNESS STRATEGY: relying on another module happening to have
 * opened a socket first. That is what the old per-area socket copies did, and
 * it is why three Meeting Intelligence subscriptions were inert in production
 * without anything failing or logging. Every entry below that says `live` is
 * asserted to correspond to a real registered channel (see the bus tests).
 *
 * ===========================================================================
 */

/** How a surface stays current. */
export type FreshnessMode =
  /** Realtime only — events arrive over the shared bus. */
  | "live"
  /** Realtime plus a slow backstop poll for server-computed rollups. */
  | "live+poll"
  /** No event stream exists; poll on an interval. */
  | "poll"
  /** Deliberately pull-only: header Refresh + post-write re-pulls. */
  | "manual";

export interface FreshnessEntry {
  /** The user-facing surface. */
  surface: string;
  mode: FreshnessMode;
  /**
   * The `RealtimeChannel.name` this surface subscribes to, for `live` and
   * `live+poll` surfaces. `null` for pull-only surfaces.
   */
  channel: string | null;
  /** Poll interval in ms for `poll` / `live+poll`; `null` otherwise. */
  pollMs: number | null;
  /** Why this mode, and what the payload does when it arrives. */
  note: string;
}

/**
 * The policy, per surface. This table is the documentation AND the test
 * fixture: `realtimeBus.test.ts` asserts every `channel` named here is a real
 * registered channel, so the doc cannot rot into a lie.
 */
export const FRESHNESS_POLICY: readonly FreshnessEntry[] = [
  // ---- LIVE ---------------------------------------------------------------
  {
    surface: "Clients list",
    mode: "live",
    channel: "clients-crud",
    pollMs: null,
    note:
      "`clients` CRUD patches the grid row in place (debounced). Other staff " +
      "edit clients concurrently, so this must not be pull-only.",
  },
  {
    surface: "Client 360 (ClientDetail)",
    mode: "live",
    channel: "clients-crud",
    pollMs: null,
    note:
      "clients + client-modules + client-tools + client-contacts CRUD, scoped " +
      "to the open clientId, applied in place. Same channel as the list — the " +
      "list simply selects the `clients` rows out of it. Since #3820 also the " +
      "Whiteboard tab (whiteboard-rows by clientId, whiteboard-cells by the " +
      "lane they sit on), the Activity timeline's communications and the " +
      "Relationship ledger (relationship-items by subject): the tabs hold " +
      "their own copies, so an event not applied by ClientDetailStore never " +
      "reaches the screen. What the meeting pipeline writes straight to the " +
      "ledger (relationship signals) is re-read on meeting-intel's " +
      "`meeting-analyzed` (Gantry) or `meeting-enriched` (HQ's own pass, " +
      "whose `meeting-processed` fires before its signals exist). Its " +
      "Meetings tab is re-read on `meeting-mapped` naming the client (a " +
      "meeting mapped to it, or re-mapped away from it; 2026-11-02).",
  },
  {
    surface: "Modules list and Module 360 (ModuleDetail)",
    mode: "live",
    channel: "modules-crud",
    pollMs: null,
    note:
      "module-catalog / module-owners / module-governance / module-releases " +
      "CRUD. The list patches catalog rows in place and re-reads its rollups " +
      "for the other three; Module 360 applies events for the open " +
      "governedSlug. The Coordinator sync writes through these services, so " +
      "a sync run streams in like an edit. Until #3820 this area owned a " +
      "socket of its own and heard nothing: the API published none of them.",
  },
  {
    surface: "Whiteboard",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "Lanes, week notes, weekly entries and deal statuses are the " +
      "`whiteboard-rows` / `whiteboard-cells` / `whiteboard-items` / " +
      "`whiteboard-statuses` ServiceStores, whose own Feathers CRUD listeners " +
      "merge every event; WhiteboardStore's lists (the board, My week, " +
      "Overdue & slipping) are computed over them, so no bus channel is " +
      "needed for them. What it does need is the API publishing those paths " +
      "to staff (STAFF_BROADCAST_CRUD_PATHS — absent until #3820, when every " +
      "edit reached only its author). Its load drops lanes, in-window notes " +
      "and entries the answer no longer holds (shared/findAndEvict), so a " +
      "removal this tab missed does not survive a Refresh (#2654). The lanes' " +
      "accepted work (`whiteboard-lane-work`, derived, no events) is re-read " +
      "when meeting-intel says an assignment was accepted, completed or " +
      "rejected, or moved to another module or client (set-module, a " +
      "retarget; 2026-11-02) — debounced — and on every load; that re-read " +
      "is what the channel named here is for.",
  },
  {
    surface: "DevOps · Pipelines board",
    mode: "live",
    channel: "devops-events",
    pollMs: null,
    note:
      "Azure webhooks arrive as `devops-event`; the build row is patched in " +
      "place. A row that is not loaded triggers one scoped re-pull.",
  },
  {
    surface: "DevOps · Work items",
    mode: "live",
    channel: "devops-events",
    pollMs: null,
    note: "Same stream, `workItem` entities only; state/fields patched in place.",
  },
  {
    surface: "Meeting Intelligence · Meetings list",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "transcript-received / meeting-processed trigger a reload (the events " +
      "are notifications, not records, so there is nothing to patch), and " +
      "since 2026-11-02 meeting-mapped: a meeting mapped to a client changes " +
      "its Client column, and under Gantry authority nothing else says so.",
  },
  {
    surface: "Meeting Intelligence · Internal meetings list",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "Same stream and same reason as the Meetings list: an internal meeting " +
      "is analysed after it is marked, so transcript-received / " +
      "meeting-processed trigger a reload. BOTH halves of the narrowing are " +
      "deliberate and neither is redundant: the load ASKS the server for " +
      "`mappingStatus=internal` (read-ai-meetings supports it) AND re-applies " +
      "the predicate to whatever comes back. The client-side pass is what " +
      "keeps the page correct against a server that ignores the param; the " +
      "server-side filter is what keeps the 500-row cap measuring INTERNAL " +
      "meetings. Delete the server-side filter and the page downloads 500 " +
      "mixed meetings, the cap bites on the wrong set, and the internal count " +
      "silently under-reports — with the page still looking right. The reload " +
      "then re-reads HQ's own `meeting-analysis` rows for the loaded ids. " +
      "IT ALSO TAKES `meeting-marked-internal` (C7, 2026-09-03), which is the " +
      "event that puts a row on this page at all: until the API emitted it " +
      "this surface was registered live while having no way to hear its own " +
      "defining transition, so a newly marked meeting appeared only when some " +
      "LATER transcript or processing event happened to arrive. And " +
      "`meeting-unmarked-internal` (2026-09-28), which takes a row OFF it.",
  },
  {
    surface: "Meeting Intelligence · Meeting detail",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "meeting-processed OR meeting-analyzed for the OPEN meetingId only, " +
      "then a reload. The second is the producer's stored result push " +
      "(`meeting-analysis-result meeting-analyzed`) — once Gantry is the " +
      "analysis authority it is the only event that says this meeting's " +
      "analysis arrived. Since #3820 also a colleague's action on its items: " +
      "`read-ai-action-items patched` (close/reopen, the item row) and " +
      "`created` (a bulk close naming the meeting, or a stale sweep that " +
      "closed something), and assignment-accepted/-rejected/-completed (and, " +
      "since #3820's finish, -module-changed) whose assignmentId is one of " +
      "this meeting's items; and, for this meeting, `meeting-enriched` (HQ's " +
      "own pass wrote its analyses, after `meeting-processed`), " +
      "`meeting-distributed` (its items were routed: the assignments exist) " +
      "and `meeting-mapped` (mapped to a client, 2026-11-02). " +
      "One predicate decides it for the page's tabs (meetingEventScope.ts " +
      "`isAboutMeeting`).",
  },
  {
    surface: "Meeting Intelligence · Meeting detail → Review",
    mode: "live",
    channel: "meeting-review",
    pollMs: null,
    note:
      "A review is built for its reader (who decides, their own verdicts), so " +
      "none ever rides the socket. The API sends `meeting-reviews " +
      "review-changed` with the meeting's id ALONE, and only to the people who " +
      "may open that review at that moment — its audience and operators; for " +
      "a private meeting its members and administrators — on a response or " +
      "answer recorded, the window opening, a close pass deciding, an " +
      "assignment moved into another module (set-module, an accept with a " +
      "module edit, or a retarget of accepted work and its undo — who " +
      "decides, and so who may open it, moves too), and " +
      "since 2026-11-02 on what the review shows changing ELSEWHERE: a " +
      "question it holds answered (in the Inquiry Queue, by the client, on " +
      "another review), released, withdrawn, re-assigned, re-worded or filed; " +
      "an item's owner moved (reassign); and who is involved — a module's " +
      "owners, the client's confirmed staff (each for every review someone " +
      "can still act on: open or closing, or holding an open question) and " +
      "the meeting's client (mapped). A meeting's participants are written " +
      "when it is ingested, before a review exists, and never after. The " +
      "open page re-reads its own view (meetingReviewLive.ts, one read per " +
      "burst) — a page the review had turned away included, so someone newly " +
      "involved sees it open. It also re-reads on the meeting-intel events " +
      "Meeting detail follows (`isAboutMeeting`), unless the review refused " +
      "this person.",
  },
  {
    surface: "Meeting Intelligence · Partner detail → Relationship ledger",
    mode: "live",
    channel: "clients-crud",
    pollMs: null,
    note:
      "The page holds its own copy, so it re-reads (one read per burst, " +
      "partnerLedgerLive.ts) on a `relationship-items` event for a row of " +
      "THIS partner — the clients-crud translator's, already published to " +
      "staff — and on the pipeline saying its relationship signals are " +
      "written (it writes them straight to the table): meeting-intel's " +
      "`meeting-analyzed` that wrote any (Gantry), or `meeting-enriched` " +
      "(HQ's own pass; never its `meeting-processed`, which fires before the " +
      "signals exist). The read goes through shared/findAndEvict, so a row removed " +
      "while this tab missed the event leaves the shared cache as well as the " +
      "page (#3820, #2654).",
  },
  {
    surface: "Client 360 · Deliverables \"Tasks\" chip",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "Counts the client's `read-ai-assignments` per module. Re-counts (one " +
      "read per burst) on assignment-accepted/-rejected/-completed/" +
      "-module-changed for an assignment the last count read (or a module " +
      "move into one of this client's modules), and on `meeting-distributed` " +
      "(or `meeting-analyzed`) for this client's meeting — a run routed its " +
      "items, on any path: Gantry's push, HQ's own pass, a question's answer " +
      "releasing held work. The notices name an assignment, never its " +
      "client, hence the match (#3820). Since 2026-11-02 also a retarget of " +
      "accepted work and its undo (-module-changed: the module it left, and " +
      "both clients when it moved to another client's own work) and " +
      "meeting-mappings' `meeting-mapped` naming this client — a meeting " +
      "mapped here takes the work it had while it had no client with it, " +
      "and that work was in no earlier count's read, so a later notice " +
      "about it would not have matched — or naming it as the client a " +
      "meeting was re-mapped away from.",
  },
  {
    surface: "Meeting Intelligence · Work status",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "assignment-accepted / assignment-rejected / assignment-completed / " +
      "assignment-module-changed (set-module, a retarget of accepted work " +
      "or its undo), meeting-distributed (a run handed a meeting's items " +
      "out: new rows exist) and meeting-mapped (a meeting's work moved to " +
      "its new client) trigger a reload (ReviewQueueStore " +
      "WORK_STATUS_RELOAD_KINDS; the last three since 2026-11-02). Each is " +
      "narrowed by the API to the people who may see a private meeting. " +
      "`assignment-approved` was removed with approval on 2026-09-18 and no " +
      "longer fires. Not announced to this page: work moved to another " +
      "PERSON (reassign) — a Refresh shows it.",
  },
  {
    surface: "Meeting Intelligence · Mapping queue",
    mode: "live",
    channel: "meeting-intel",
    pollMs: null,
    note:
      "transcript-received / meeting-processed / meeting-marked-internal " +
      "trigger a reload, suppressed while an assign is in flight (it reloads " +
      "itself on completion). THE THIRD ONE IS LOAD-BEARING (C7, " +
      "2026-09-03): a meeting also leaves this queue by being marked " +
      "internal, and nothing announced that — `meeting-mappings` carried no " +
      "events and runPipeline's internal branch returns before " +
      "emitProcessed — so a second operator kept being offered Assign and " +
      "Internal on a row somebody had already resolved. The API now emits " +
      "`meeting-marked-internal` on `meeting-mappings`. It also reloads on " +
      "`meeting-unmarked-internal` (2026-09-28): a meeting sent back from " +
      "Internal Meetings re-enters this queue; and on `meeting-mapped` " +
      "(2026-11-02): another operator's Assign takes a row out, and under " +
      "Gantry authority the reprocess it runs announces nothing else.",
  },
  {
    surface: "Infrastructure · Client instances grid",
    mode: "live",
    channel: "instance-status",
    pollMs: null,
    note:
      "The API's reconcile sweep publishes the whole refreshed fleet every few " +
      "minutes; the grid applies the envelope in place. The CADENCE IS THE " +
      "SERVER'S — the client does not poll.",
  },
  {
    surface: "Infrastructure · Update badges (fleet grid)",
    mode: "live",
    channel: "instance-update",
    pollMs: null,
    note:
      "The registry sweep announces false→true update-availability flips; the " +
      "grid patches the affected rows in place, so a new badge appears without " +
      "a reload. The badge clears via the next status envelope or reload.",
  },
  {
    surface: "Infrastructure · Action progress (confirm / deploy / rollback / move dialogs)",
    mode: "live",
    channel: "instance-action-progress",
    pollMs: null,
    note:
      "While an action runs, the API emits one event per step (inspect, pull " +
      "with layer counts, stop, remove, create, start, health wait, outcome). " +
      "The dialog that sent it subscribes for the life of the call and shows " +
      "the step it is on; with no event received it says so rather than guess.",
  },
  {
    surface: "Infrastructure · Instance detail (logs / stats / exec)",
    mode: "live",
    channel: null,
    pollMs: null,
    note:
      "Raw bidirectional Socket.io streams on the shared connection " +
      "(`instance:logs:*`, `instance:stats:*`, `instance:exec:*`), leased from " +
      "the bus rather than translated. Staff-only; torn down on stop AND on " +
      "socket disconnect.",
  },

  {
    surface: "Tickets desk, and Client 360's Tickets tab",
    mode: "live",
    channel: "tickets-crud",
    pollMs: null,
    note:
      "Both hold a snapshot they re-read after their own writes; since #3820 " +
      "a burst of `tickets` events re-reads it too (views/tickets/" +
      "ticketsLive.ts), and the desk re-reads the open ticket's timeline " +
      "when it was the one that moved. The API sends a ticket row only to " +
      "the accounts its read admits (requester, assignee, the team, " +
      "operators), so nothing arrives that this person could not read.",
  },

  // ---- LIVE + POLL --------------------------------------------------------
  {
    surface: "Command Center (home dashboard)",
    mode: "live+poll",
    channel: "devops-events",
    pollMs: 60_000,
    note:
      "Live `devops-event` envelopes prepend rows to the activity feed for " +
      "immediacy; the 60s poll refreshes the server-COMPUTED rollup cards that " +
      "no single event can update, and clears the live rows it has absorbed.",
  },
  {
    surface: "Notification bell",
    mode: "live+poll",
    channel: "notifications-crud",
    pollMs: 60_000,
    note:
      "`notifications` CRUD on the user's personal channel; the 60s poll is a " +
      "backstop for missed events across reconnects and keeps the unread count " +
      "authoritative. Delivery is deduped by id, so double delivery is benign.",
  },

  // ---- POLL ONLY ----------------------------------------------------------
  {
    surface: "Dashboards (Developer board, landing; Sales Manager and Operations Manager boards)",
    mode: "poll",
    channel: null,
    pollMs: 60_000,
    note:
      "Widget DATA is poll-only BY THE TOOL'S DESIGN: @intrasync/dashboard " +
      "re-queries every `hq.*` source on the board's `refreshSeconds` (seeded " +
      "60s on the Developer and Operations boards, 300s on the Sales board; " +
      "floored at 15s, capped at 60s in kiosk) and on a manual Refresh. " +
      "Every source is a server-computed rollup (worst-first instance rows " +
      "from the last reconcile sweep, the live-Azure build board behind its " +
      "20s api cache, counts over devops_events / audit / integration " +
      "errors) that no single event can patch in place — the same reason the " +
      "Command Center polls. Board / instance / share / prefs ROWS arrive " +
      "through the four `dashboard-*` ServiceStores' own Feathers CRUD " +
      "listeners, not this bus; they need the api to publish on a channel " +
      "this user is joined to, which is the api host's job. Nothing here " +
      "subscribes through `realtimeBus`.",
  },
  {
    surface: "Admin · System health",
    mode: "poll",
    channel: null,
    pollMs: 30_000,
    note:
      "Liveness probes have no event stream — a stale green light is actively " +
      "misleading, so this is the one surface that polls fast.",
  },

  // ---- MANUAL -------------------------------------------------------------
  {
    surface: "Reports (the reports tool hub: catalog, runs, views, schedules, history, admin)",
    mode: "manual",
    channel: null,
    pollMs: null,
    note:
      "@intrasync/reports loads each tab on first use and re-pulls after its " +
      "own writes; a run is a request/response (`reports-runs.execute`), so " +
      "there is nothing to stream. Rows another person changes (a shared " +
      "view, a schedule, the availability switches) reach the eight " +
      "`reports-*` ServiceStores through their own Feathers CRUD listeners " +
      "when the api publishes on a channel this user is joined to (the api " +
      "host's job), not through `realtimeBus`.",
  },

  {
    surface:
      "Everything else (AI/LLM admin, Admin + Administration, Settings, " +
      "the Partners list and Partner detail's overview, Domain mappings, " +
      "Meeting sources, Delivery connections, Portainer/registry " +
      "connections, generated Service Views)",
    mode: "manual",
    channel: null,
    pollMs: null,
    note:
      "Pull-only BY DESIGN: configuration and admin surfaces change only " +
      "because the person looking at them changed them. They re-pull after " +
      "their own writes and carry a header Refresh button.",
  },
] as const;

/** Every distinct realtime channel a documented surface depends on. */
export const LIVE_CHANNEL_NAMES: readonly string[] = Array.from(
  new Set(
    FRESHNESS_POLICY.filter((e) => e.channel !== null).map((e) => e.channel!)
  )
);
